import React, { useEffect, useRef, useState } from "react";
import {
  IconChallenge,
  IconDelete,
  IconEdit,
  IconLike,
  IconLikeOutline,
  IconMore,
  IconUser,
} from "../CustomIcons";
import remarkGfm from "remark-gfm";
import ShareThis from "../utils/shareThis";
import SocialPlatformEmbed from "./socialPlatformEmbed";
import { useAuth } from "../../contexts/AuthContext";
import { deleteLeagueFeed, updateLikeCountOnFeed } from "./firestoreQuery";
import { Trash2, X } from "lucide-react";
import PostFeed from "./postFeed";
import { useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import noImg from "../../assets/images/winner-icon.png";
import { getInitialsForAvatar } from "../utils/getInitialsForAvatar";

interface FeedBlockProps {
  feed: { [key: string]: any };
  leagueId: string;
  commissionerDetails: { [key: string]: any };
  isSharedFeed?: boolean;
  leagueUrl: string;
  createdByName?: string;
  createdByImage?: string;
  setActiveTab: (tab: number) => void;
}

const FeedBlock: React.FC<FeedBlockProps> = ({
  feed,
  leagueId,
  commissionerDetails,
  isSharedFeed = false,
  leagueUrl,
  createdByName,
  createdByImage,
  setActiveTab,
}) => {
  const navigate = useNavigate();
  const authorName = feed?.authorName || "Unknown";
  const createdAt = feed?.createdAt;
  const { user } = useAuth();

  const initialLiked = (() => {
    try {
      return !!(
        user?.uid &&
        Array.isArray(feed?.likedBy) &&
        feed.likedBy.includes(user.uid)
      );
    } catch (e) {
      return false;
    }
  })();

  const [localLiked, setLocalLiked] = React.useState<boolean>(initialLiked);
  const [localCount, setLocalCount] = React.useState<number>(
    feed?.likeCounter ?? 0
  );
  const [isLiking, setIsLiking] = React.useState<boolean>(false);
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  // Sync local optimistic state if feed prop changes from realtime updates
  useEffect(() => {
    setLocalCount(feed?.likeCounter ?? 0);
    try {
      setLocalLiked(
        !!(
          user?.uid &&
          Array.isArray(feed?.likedBy) &&
          feed.likedBy.includes(user.uid)
        )
      );
    } catch (e) {
      // ignore
    }
  }, [feed?.likeCounter, feed?.likedBy, user?.uid]);

  const timeText = (() => {
    if (!createdAt) return "Just now";
    try {
      const diff = Date.now() - new Date(createdAt).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "Just now";
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 30) return `${days}d ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months}mo ago`;
      const years = Math.floor(months / 12);
      return `${years}y ago`;
    } catch (e) {
      return "some time ago";
    }
  })();
  // Helper: linkify plain text URLs into anchor tags
  const linkify = (text: string | undefined) => {
    if (!text) return "";
    // simple URL regex (http(s):// or www.)
    const urlRegex =
      /((https?:\/\/|www\.)[\w\-@:%._+~#=]{2,256}\.[a-z]{2,6}\b(?:[\w@:%_+.~#?&//=;-]*))/gi;
    return text.replace(urlRegex, (match: string) => {
      const href = match.startsWith("http") ? match : `https://${match}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    });
  };

  const handleLikeClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.uid) return; // require login
    setIsLiking(true);
    // optimistic update
    const nextLiked = !localLiked;
    setLocalLiked(nextLiked);
    setLocalCount((c) => c + (nextLiked ? 1 : -1));

    try {
      await updateLikeCountOnFeed(leagueId, feed.id, user.uid);
    } catch (error) {
      console.error("Error liking the feed:", error);
      // revert optimistic
      setLocalLiked(localLiked);
      setLocalCount(feed?.likeCounter ?? 0);
    }
    setIsLiking(false);
  };
  // More modal state
  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!moreRef.current) return;
      if (!moreRef.current.contains(ev.target as Node)) {
        setShowMore(false);
      }
    };
    if (showMore) document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [showMore]);

  const handleMoreToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMore((s) => !s);
  };

  const onDelete = () => {
    setIsConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteLeagueFeed(leagueId, feed.id);
    } catch (error) {
      console.error("Error deleting the feed:", error);
    }
    setIsDeleting(false);
  };

  const getClassNameForFeedStatus = (status: string) => {
    switch (status) {
      case "deleted":
        return "deletedFeed";
      case "expired":
        return "expiredFeed";
      default:
        return "";
    }
  };

  return (
    <>
      {!openEditModal ? (
        <div
          className={`feedBlk ${
            feed.postType === "challenge" &&
            getClassNameForFeedStatus(feed.challengeCreationStatus)
          }`}
        >
          <div className="feedBlkTop">
            <span>{timeText}</span>
            {feed?.postType === "challenge" ? (
              <div className="moreActionWrap">
                <span className="badgeCommissioner">
                  <IconChallenge /> Challenge
                </span>
              </div>
            ) : (
              <>
                {(user?.uid === feed?.createdBy ||
                  user?.uid === commissionerDetails.id) &&
                  !openEditModal &&
                  !isSharedFeed && (
                    <div className="moreActionWrap">
                      <span className="actionOption" onClick={handleMoreToggle}>
                        <IconMore />
                      </span>
                      {showMore && (
                        <div
                          className="moreOptionWrap"
                          ref={moreRef}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {user?.uid === feed?.createdBy && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMore(false);
                                setOpenEditModal(true);
                              }}
                            >
                              <IconEdit />
                              <span>Edit Post</span>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMore(false);
                              onDelete();
                            }}
                          >
                            <IconDelete />
                            <span>Delete Post</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
              </>
            )}
          </div>
          <div className="feedContent">
            {feed.postType !== "challenge" && (
              <div className="userBlkWrap">
                <div className="userPic">
                  {feed?.authorPic ? (
                    <img src={feed?.authorPic} alt="" />
                  ) : (
                    <IconUser width={40} />
                  )}
                </div>
                <div className="userCont">
                  <div className="userHeading">{authorName}</div>
                </div>
              </div>
            )}
            {/* Render main text content first */}
            {feed?.content && (
              <div
                className="feedText"
                dangerouslySetInnerHTML={{
                  __html: linkify(feed.content),
                }}
              ></div>
            )}

            {/* Render image if present */}
            {feed?.imageUrl && (
              <div className="feedMedia">
                <img
                  src={feed.imageUrl}
                  alt={
                    (feed?.content && String(feed.content).slice(0, 80)) ||
                    "feed image"
                  }
                  style={{ maxWidth: "100%" }}
                />
              </div>
            )}

            {/* Render video if present */}
            {feed?.videoUrl && (
              <div className="feedMedia video">
                <video
                  controls
                  src={feed.videoUrl}
                  style={{ maxWidth: "100%" }}
                />
              </div>
            )}

            {/* If a social link is present, embed it */}
            {feed?.link && (
              <div className="feedEmbed">
                <SocialPlatformEmbed
                  url={feed.link}
                  platform={feed.platform as any}
                  width={"100%"}
                  height={feed?.platform === "youtube" ? 300 : "100%"}
                />
              </div>
            )}

            {feed?.postType === "challenge" && (
              <>
                <div className="challengeHeading">{feed?.challengeTitle}</div>
                <div className="challengeTopBlk">
                  <div className="challengeImg">
                    <img
                      src={
                        feed?.challengeImageUrl
                          ? feed?.challengeImageUrl
                          : noImg
                      }
                      alt=""
                    />
                  </div>
                  <div className="prose">
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {feed?.challengeDescription}
                    </Markdown>
                  </div>
                </div>
                <div className="createdUserDetail">
                  <div className="userPic">
                    {createdByImage ? (
                      <img src={createdByImage} alt={createdByName} />
                    ) : (
                      <div className="userInitials">
                        {getInitialsForAvatar(createdByName ?? "")}
                      </div>
                    )}
                  </div>
                  <div className="userInfo">
                    <span>Created by</span>
                    <strong>{createdByName}</strong>
                  </div>
                </div>
              </>
            )}
          </div>
          {feed.postType !== "challenge" && (
            <div className="feedBlkBottom">
              <button
                className="likeOption"
                onClick={handleLikeClick}
                disabled={isLiking}
              >
                {localLiked ? <IconLike /> : <IconLikeOutline />}
                <span>{localCount ?? 0}</span>
              </button>
              <ShareThis feedId={feed?.id} leagueUrl={leagueUrl} />
            </div>
          )}
          {feed.postType === "challenge" &&
            !["deleted", "expired"].includes(feed.challengeCreationStatus) && (
              <div className="btnWrap">
                <button
                  type="button"
                  className="btnStyle btnFilled"
                  onClick={() => {
                    setActiveTab(2);
                    navigate(`/${leagueUrl}/challenges`);
                  }}
                >
                  View Details
                </button>
              </div>
            )}
        </div>
      ) : (
        <div className="feedBlk">
          <div className="feedBlkTop">
            <span>{timeText}</span>
          </div>
          <div className="feedContent">
            <PostFeed
              feedToEdit={{ ...feed, leagueId }}
              handleOnCancelEdit={() => setOpenEditModal(false)}
            />
          </div>
        </div>
      )}

      {isConfirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full relative">
            <button
              onClick={() => setIsConfirmDelete(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="bg-red-100 p-3 rounded-full">
                {<Trash2 className="w-6 h-6 text-red-600" />}
              </div>
              <h4 className="text-xl font-semibold">Delete Confirmation</h4>
              <p className="text-sm text-gray-600">
                {
                  "Are you sure you want to delete this post? This action cannot be undone."
                }
              </p>
              <div className="flex gap-4 mt-4">
                <button
                  onClick={() => setIsConfirmDelete(false)}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeleting}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedBlock;
